import React from "react";

import ZestComponent from "./ZestComponent";
import type { SegmentColor } from "./types";

// A segmented status bar: renders each of `segments` ({ key, label, value,
// color }) as a slice of a full-width bar, sized by flex-grow so its share of
// the bar is proportional to its value, while MIN_SEGMENT_WIDTH still holds
// as a floor — a slice narrower than the bar is tall can't fit its
// rounded-full end-cap, so the container's own rounding clips its background
// away instead of capping it. When `remainderLabel` is given, whatever's left
// of `total` after summing the segments' values (e.g. counts not yet
// actioned) renders as one more segment, always last, in a fixed muted
// style — so the bar always accounts for the full `total`, not just the
// segments explicitly passed in. Without a `remainderLabel`, any such
// leftover renders as blank space instead.

const SEGMENT_COLORS: Record<SegmentColor, string[]> = {
  orange: ["bg-brand", "text-negative"],
  green: ["bg-success", "text-negative"],
  red: ["bg-danger", "text-negative"],
  slate: ["bg-light", "text-primary"],
};

const REMAINDER_CLASSES = "bg-lighter text-primary";

// Matches the bar's own h-8 (2rem): a slice needs to be at least as wide as
// the bar is tall for its rounded-full end-cap to render as a full curve
// rather than getting clipped.
const MIN_SEGMENT_WIDTH = "2rem";

const segmentClasses = (color: SegmentColor): string => (SEGMENT_COLORS[color] ?? SEGMENT_COLORS.slate).join(" ");

export interface StatusBarSegment {
  key?: string;
  label: string;
  value: number;
  color: SegmentColor;
  // Draws a divider on the segment's trailing edge — for adjacent segments
  // that share a color and would otherwise blend into one another.
  borderRight?: boolean;
}

export interface StatusBarProps {
  segments: StatusBarSegment[];
  total?: number;
  remainderLabel?: string;
}

interface SegmentProps {
  label: string;
  value: number;
  className: string;
}

// `flex-basis: 0` makes flex-grow distribute space purely by these value
// ratios rather than the label's own width, while `min-width` still wins
// over that distribution as a hard floor.
const Segment = ({ label, value, className }: SegmentProps): JSX.Element => (
  <div
    className={`overflow-hidden whitespace-nowrap ${className}`}
    style={{ flex: `${value} 1 0%`, minWidth: MIN_SEGMENT_WIDTH }}
  >
    <span className="block px-3 truncate text-sm font-bold leading-8">
      {label} {value}
    </span>
  </div>
);

// Only positive values are rendered (see the `value <= 0` skip below), so only
// those count towards the implicit total and the remainder — otherwise a
// negative value would silently shrink every other segment's width.
const sumValues = (segments: StatusBarSegment[]): number =>
  segments.reduce((sum, segment) => sum + Math.max(segment.value, 0), 0);

const StatusBar = ({ segments, total = sumValues(segments), remainderLabel }: StatusBarProps): JSX.Element => {
  const shortfall = Math.max(total - sumValues(segments), 0);
  const remainder = remainderLabel ? shortfall : 0;
  // Without a remainderLabel, any shortfall still has to claim its share of
  // the bar's width — as invisible flex space, not a floored, labeled
  // segment — so the real segments don't stretch to fill a total they don't
  // account for.
  const blankSpace = remainderLabel ? 0 : shortfall;
  const lastValueIndex = segments.reduce((last, segment, index) => (segment.value > 0 ? index : last), -1);

  return (
    <ZestComponent name="status-bar" className="flex h-8 w-full overflow-hidden rounded-full bg-lighter">
      {segments.map((segment, index) => {
        if (segment.value <= 0) return null;

        // The segment right before the remainder isn't the bar's true edge
        // anymore, so it needs its own rounded cap to read as "filled" —
        // without the remainder, the container's own rounding already
        // covers it.
        const className = [
          segmentClasses(segment.color),
          segment.borderRight && "border-r border-white/40",
          index === lastValueIndex && "rounded-r-full",
        ]
          .filter(Boolean)
          .join(" ");

        return <Segment key={segment.key ?? index} label={segment.label} value={segment.value} className={className} />;
      })}
      {remainder > 0 && remainderLabel && (
        <Segment label={remainderLabel} value={remainder} className={REMAINDER_CLASSES} />
      )}
      {blankSpace > 0 && <div aria-hidden="true" style={{ flex: `${blankSpace} 1 0%` }} />}
    </ZestComponent>
  );
};

export default StatusBar;
